import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import { ErrorOutline } from '@mui/icons-material'

import CampaignDetail from '../components/CampaignDetail'
import { useToast } from '../hooks'
import { useGlobalStoreActions } from '../../store/globalStore'
import { getCampaign } from '../../mailTemplate/src/services/B2BServices'

const MissingParametersError = () => {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          px: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            p: 8,
            maxWidth: 600,
            width: '100%',
            backgroundColor: '#fef7f7',
            border: '2px solid #ffcdd2',
            borderRadius: 3
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                backgroundColor: '#f44336',
                borderRadius: '50%',
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ErrorOutline sx={{ color: 'white', fontSize: 48 }}/>
            </Box>
          </Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#d32f2f'
            }}
          >
            {t('mailTemplate.campaigns.accessErrorTitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#424242',
              lineHeight: 1.6
            }}
          >
            {t('mailTemplate.campaigns.campaignNotFoundError')}
          </Typography>
        </Paper>
      </Box>
    </Box>
  )
}

const LoadingState = () => (
  <Box sx={{ p: 2 }}>
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 400
    }}>
      <CircularProgress size={60}/>
    </Box>
  </Box>
)

const CampaignNotFound = () => {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          px: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            p: 8,
            maxWidth: 600,
            width: '100%',
            backgroundColor: '#fef7f7',
            border: '2px solid #ffcdd2',
            borderRadius: 3
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#d32f2f'
            }}
          >
            {t('mailTemplate.campaigns.campaignNotFound')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#424242',
              lineHeight: 1.6
            }}
          >
            {t('mailTemplate.campaigns.campaignNotFoundDescription')}
          </Typography>
        </Paper>
      </Box>
    </Box>
  )
}

const CampaignDetailPage = () => {
  const { t } = useTranslation()

  const { showError } = useToast()

  const [searchParams] = useSearchParams()

  const [campaign, setCampaign] = useState(null)
  const [hasFetched, setHasFetched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { setCampaignStatus } = useGlobalStoreActions()

  const b2bAccountId = searchParams.get('b2bAccountId')
  const b2bCampaignId = searchParams.get('b2bCampaignId')

  const fetchCampaign = useCallback(async () => {
    if (!b2bCampaignId) return
    setIsLoading(true)
    try {
      const res = await getCampaign(b2bCampaignId)
      setCampaign(res.data)
      if (res.data?.status) {
        setCampaignStatus(b2bCampaignId, res.data.status)
      }
    } catch (error) {
      showError(t('mailTemplate.campaigns.errors.failedToLoadCampaigns'))
    } finally {
      setIsLoading(false)
      setHasFetched(true)
    }
  }, [b2bCampaignId, setCampaignStatus, showError, t])

  const refreshCampaign = useCallback(async () => {
    try {
      await fetchCampaign()
    } catch (error) {
      console.error('Failed to refresh campaign data:', error)
    }
  }, [fetchCampaign])

  useEffect(() => {
    const loadCampaign = async () => {
      if (b2bCampaignId && !hasFetched && !isLoading) {
        await fetchCampaign()
      }
    }

    loadCampaign()
  }, [b2bCampaignId, hasFetched, isLoading, fetchCampaign])

  if (!b2bAccountId || !b2bCampaignId) {
    return <MissingParametersError/>
  }

  if (isLoading || (!hasFetched && b2bAccountId)) {
    return <LoadingState/>
  }
  if (!campaign) {
    return <CampaignNotFound/>
  }

  return <CampaignDetail campaign={campaign} onCampaignUpdate={refreshCampaign}/>
}

export default CampaignDetailPage
